import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { Select } from 'shared/ui/Inputs/Select/Select';
import type { SelectProps } from '../Inputs/Select/Select';
import { useRules } from './hooks';

export type SelectFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<SelectProps, 'value' | 'defaultValue'>;

/**
 * SelectField component
 *
 * @example
 * ```tsx
 * <SelectField name="field.name" required data={[
 *  { label: 'Option 1', value: 'option1' },
 *  { label: 'Option 2', value: 'option2' },
 * ]}/>
 * ```
 *
 * @public
 * @param name - field name
 * @param control - form control
 * @param defaultValue - default value
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param onChange - onChange event
 * @param required - is field required
 * @param props - other props
 */
export function SelectField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  required,
  ...props
}: SelectFieldProps<T>) {
  const innerRules = useRules({ required, ...rules });

  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules: innerRules,
    shouldUnregister,
  });

  return (
    <Select
      withinPortal
      error={fieldState.error?.message}
      value={value}
      onChange={e => {
        fieldOnChange(e);
        onChange?.(e);
      }}
      {...field}
      {...props}
    />
  );
}
